<template>
    <div id="page-team-dashboard" data-page="team-dashboard" class="team-management-page py-4">
        <div id="page-subordinates" data-page="subordinates" class="position-absolute" style="top:0;left:0;width:1px;height:1px;opacity:0.01;pointer-events:none;"></div>
        <b-container v-if="!isDevEnv" fluid>
            <!-- Page Header (Beta / Production Coming Next State) -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom team-page-header">
                <div>
                    <h3 class="font-weight-bold mb-1 d-flex align-items-center team-title">
                        <i class="bi bi-speedometer2 text-primary mr-2"></i>Team Dashboard
                        <b-badge variant="info" class="ml-3 px-2 py-1" style="font-size: 0.72rem; vertical-align: middle;" data-test="badge-team-coming-next">
                            <i class="bi bi-clock-history mr-1"></i>Coming Next
                        </b-badge>
                    </h3>
                    <div class="text-muted small">
                        Squad sprint deliverables, capacity burn-down, and absence schedule tracking
                    </div>
                </div>
            </div>

            <!-- Coming Next Hero Card -->
            <div class="team-coming-soon-wrapper py-4" data-test="team-coming-next-card">
                <b-card class="border-0 shadow-sm text-center py-5 px-4 mx-auto team-coming-soon-card" style="max-width: 780px; border-radius: 16px;">
                    <div class="coming-soon-icon-badge mx-auto mb-3 d-flex align-items-center justify-content-center">
                        <i class="bi bi-people-fill text-info" style="font-size: 2.2rem;"></i>
                    </div>
                    <span class="badge badge-pill px-3 py-1 mb-3 font-weight-bold text-uppercase coming-soon-pill">
                        <i class="bi bi-stars mr-1"></i>Coming Next on the Roadmap
                    </span>
                    <h4 class="font-weight-bold mb-2 team-title">Team &amp; Capacity Management is Coming Next</h4>
                    <p class="text-muted mx-auto mb-4" style="max-width: 600px; font-size: 0.95rem; line-height: 1.6;">
                        During the Public Beta, VelocyTrack is focused on perfecting the individual <strong>Solo Engineering</strong> workflow. Multi-seat Team workspaces and manager capacity analytics are currently in active development and will be unlocked in an upcoming release.
                    </p>

                    <b-row class="text-left mt-4 pt-3 border-top">
                        <b-col md="6" class="mb-3">
                            <div class="d-flex align-items-start p-3 rounded feature-preview-card h-100">
                                <i class="bi bi-person-badge text-primary mr-3 mt-1" style="font-size: 1.35rem;"></i>
                                <div>
                                    <div class="font-weight-bold small mb-1 feature-preview-title">Member Profiles &amp; Timesheet Insights</div>
                                    <div class="text-muted" style="font-size: 0.8rem;">Track monthly logged hours, daily velocity sparklines, and activity breakdowns across your engineering squad.</div>
                                </div>
                            </div>
                        </b-col>
                        <b-col md="6" class="mb-3">
                            <div class="d-flex align-items-start p-3 rounded feature-preview-card h-100">
                                <i class="bi bi-bar-chart-steps text-info mr-3 mt-1" style="font-size: 1.35rem;"></i>
                                <div>
                                    <div class="font-weight-bold small mb-1 feature-preview-title">Capacity &amp; Sprint Burn-Down</div>
                                    <div class="text-muted" style="font-size: 0.8rem;">Compare allocated workload against net available capacity and monitor ideal vs. actual remaining effort.</div>
                                </div>
                            </div>
                        </b-col>
                        <b-col md="6" class="mb-3 mb-md-0">
                            <div class="d-flex align-items-start p-3 rounded feature-preview-card h-100">
                                <i class="bi bi-kanban text-success mr-3 mt-1" style="font-size: 1.35rem;"></i>
                                <div>
                                    <div class="font-weight-bold small mb-1 feature-preview-title">Work Item Commitments</div>
                                    <div class="text-muted" style="font-size: 0.8rem;">Audit sprint commitments, completion ratios, and unestimated tasks across every team member.</div>
                                </div>
                            </div>
                        </b-col>
                        <b-col md="6">
                            <div class="d-flex align-items-start p-3 rounded feature-preview-card h-100">
                                <i class="bi bi-calendar2-week text-warning mr-3 mt-1" style="font-size: 1.35rem;"></i>
                                <div>
                                    <div class="font-weight-bold small mb-1 feature-preview-title">Absence &amp; Holiday Planner</div>
                                    <div class="text-muted" style="font-size: 0.8rem;">Coordinate team PTO, sick leave, and regional public holidays in a unified availability timeline.</div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
            </div>
        </b-container>

        <!-- Missing Team Space Warning Card (matches Timelog, Backlog, PRs, Branches, Discussions) -->
        <card-missing-source
            v-else-if="!availableWorkspaces.length && !loadingAll"
            source-type="team"
            feature-name="Team"
            data-test="banner-no-team-space"
        />

        <!-- Service Unavailable Placeholder -->
        <div v-else-if="isServiceUnavailable" class="text-center py-5 text-muted">
            <i class="bi bi-cloud-slash fa-2x mb-2 d-block text-warning"></i>
            <div class="font-weight-bold mb-1">Service is currently unreachable</div>
            <span class="small">Team analytics will be loaded automatically once the service is restored.</span>
        </div>

        <b-container v-else fluid>
            <!-- Page Header -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom team-page-header">
                <div>
                    <div class="d-flex align-items-center flex-wrap">
                        <h3 class="font-weight-bold mb-1 mr-3 team-title">
                            <i class="bi bi-speedometer2 text-primary mr-2"></i>Team Dashboard
                        </h3>
                        <b-badge :variant="isCurrentTeamAdmin ? 'primary' : 'secondary'" class="px-2 py-1 mr-2 mb-1" data-test="badge-current-team-role">
                            <i class="bi bi-shield-check mr-1"></i>{{ currentTeamRoleLabel }}
                        </b-badge>
                        <b-badge v-if="activeTeamObj" variant="info" class="px-2 py-1 mb-1" data-test="badge-team-seat-summary">
                            <i class="bi bi-layers-fill mr-1"></i>{{ activeTeamObj.activeMembersCount ?? 0 }} / {{ activeTeamObj.seatLimit || 5 }} Seats
                        </b-badge>
                    </div>
                    <div class="text-muted small">
                        Squad sprint deliverables, capacity burn-down, and absence schedule tracking across shared Project Spaces
                    </div>
                </div>

                <!-- Team Switcher, Shared Project Space Filter & Manage Squad CTA -->
                <div class="d-flex flex-wrap align-items-center mt-3 mt-lg-0">
                    <div class="d-flex align-items-center mr-2 mb-2 mb-sm-0" v-if="availableWorkspaces.length">
                        <label class="small font-weight-bold text-muted text-uppercase mr-2 mb-0">Team:</label>
                        <b-form-select
                            v-model="selectedWorkspaceId"
                            size="sm"
                            style="min-width: 185px;"
                            data-test="select-active-team"
                            @change="onTeamSelectionChange"
                        >
                            <b-form-select-option
                                v-for="ws in availableWorkspaces"
                                :key="ws.workspaceId"
                                :value="ws.workspaceId"
                            >
                                {{ ws.name }} ({{ formatTeamRole(ws.currentUserRole) }})
                            </b-form-select-option>
                        </b-form-select>
                    </div>

                    <div class="d-flex align-items-center mr-2 mb-2 mb-sm-0">
                        <label class="small font-weight-bold text-muted text-uppercase mr-2 mb-0">Project Space:</label>
                        <b-form-select
                            v-model="selectedProjectSpaceId"
                            size="sm"
                            style="min-width: 200px;"
                            data-test="select-team-project-space"
                            @change="onProjectSpaceSelectionChange"
                        >
                            <b-form-select-option :value="null">All Shared Project Spaces</b-form-select-option>
                            <b-form-select-option
                                v-for="sp in activeTeamProjectSpaces"
                                :key="sp.id || sp.templateProjectSpaceId"
                                :value="sp.memberProjectSpaceId || sp.templateProjectSpaceId || sp.id"
                            >
                                {{ sp.projectSpaceName }}
                            </b-form-select-option>
                        </b-form-select>
                    </div>

                    <b-button
                        variant="outline-primary"
                        size="sm"
                        class="font-weight-bold mb-2 mb-sm-0"
                        data-test="btn-goto-manage-team"
                        @click="goToManageTeams"
                    >
                        <i class="bi bi-gear-fill mr-1"></i>Manage Squad &amp; Invites
                    </b-button>
                </div>
            </div>

            <!-- Role Transparency Notice for Standard Members -->
            <b-alert
                v-if="!isCurrentTeamAdmin"
                show
                variant="info"
                class="mb-3 d-flex align-items-center justify-content-between flex-wrap"
                data-test="alert-role-transparency"
            >
                <div>
                    <i class="bi bi-shield-lock-fill mr-2"></i>
                    <strong>Role-Aware Privacy View:</strong> As a squad <strong>{{ currentTeamRoleLabel }}</strong>, you have read-only visibility into squad capacity burn-down, deliverables, and leave schedules. Squad membership and seat configuration are managed in Configuration.
                </div>
            </b-alert>

            <!-- SHARED SPRINT SCOPE (Drives Sections 1, 2 & 3) -->
            <div class="mb-4">
                <picker-sprints
                    connection-context="backlog"
                    scope-subtitle="Select an active Azure DevOps sprint to analyze capacity burn-down, deliverables, and leave schedules."
                    @sprint-picked="onSprintPicked"
                ></picker-sprints>
            </div>

            <!-- SECTION 1: CAPACITY & SPRINT BURN-DOWN -->
            <b-card no-body class="shadow-sm border mb-4 config-section-card" id="section-team-capacity">
                <div
                    class="card-header border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center section-accordion-header"
                    :class="{ 'border-bottom': isSectionExpanded('capacity'), 'border-bottom-0': !isSectionExpanded('capacity') }"
                    role="button"
                    tabindex="0"
                    :aria-expanded="isSectionExpanded('capacity') ? 'true' : 'false'"
                    aria-controls="collapse-team-capacity"
                    @click="toggleSection('capacity')"
                    @keydown.enter.prevent="toggleSection('capacity')"
                    @keydown.space.prevent="toggleSection('capacity')"
                    data-test="header-team-capacity"
                >
                    <div class="d-flex align-items-center">
                        <span class="section-step-badge mr-3">1</span>
                        <div class="section-icon mr-2 text-info">
                            <i class="bi bi-speedometer2 fa-lg"></i>
                        </div>
                        <div>
                            <h5 class="font-weight-bold text-dark mb-0 d-flex align-items-center">
                                Capacity &amp; Burn-Down
                                <b-badge v-if="capacityData" pill variant="info" class="ml-2 px-2 font-weight-bold">{{ capacityData.members.length }} Members</b-badge>
                            </h5>
                            <div class="small text-muted">
                                Compare allocated workload against net available capacity and monitor ideal vs. actual remaining effort.
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center mt-2 mt-md-0" @click.stop>
                        <b-button size="sm" variant="outline-primary" @click="loadCapacityReportsAsync" :disabled="capacityLoading" title="Refresh capacity metrics">
                            <b-spinner v-if="capacityLoading" small></b-spinner>
                            <span v-else><i class="bi bi-arrow-clockwise mr-1"></i>Refresh Capacity</span>
                        </b-button>

                        <!-- Accordion Expand/Collapse Indicator -->
                        <div
                            class="accordion-toggle-icon ml-3 pl-3 border-left"
                            data-test="btn-toggle-section-capacity"
                            :title="isSectionExpanded('capacity') ? 'Collapse section' : 'Expand section'"
                            @click="toggleSection('capacity')"
                        >
                            <i class="bi bi-chevron-down accordion-chevron" :class="{ 'is-expanded': isSectionExpanded('capacity') }"></i>
                        </div>
                    </div>
                </div>

                <b-collapse id="collapse-team-capacity" v-model="expandedSections.capacity">

                <div class="card-body p-4 team-capacity-section" data-test="tab-team-capacity">
                    <!-- Capacity Loading / Content -->
                    <div v-if="capacityLoading" class="team-loading text-center py-5">
                        <load-spinner text="Calculating team capacity & burn-down..." size="lg" />
                    </div>

                    <div v-else-if="capacityData">
                        <!-- 4 Stat Summary Cards -->
                        <b-row class="mb-4">
                            <!-- Team Completion -->
                            <b-col md="6" lg="3" class="mb-3">
                                <b-card class="stat-card shadow-sm border-0 h-100">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <span class="text-muted small text-uppercase font-weight-bold">Team Completion</span>
                                        <b-badge :variant="getCompletionBadgeVariant(capacityData.overallCompletionPercentage)">
                                            {{ capacityData.overallCompletionPercentage }}%
                                        </b-badge>
                                    </div>
                                    <div class="display-4 font-weight-bold text-dark my-1">
                                        {{ capacityData.overallCompletionPercentage }}%
                                    </div>
                                    <b-progress
                                        :value="capacityData.overallCompletionPercentage"
                                        :max="100"
                                        :variant="getCompletionProgressVariant(capacityData.overallCompletionPercentage)"
                                        height="8px"
                                        class="mt-2 rounded"
                                    ></b-progress>
                                    <div class="small text-muted mt-2">
                                        {{ capacityData.totalWorkDays }} working days in period
                                    </div>
                                </b-card>
                            </b-col>

                            <!-- Total Expected -->
                            <b-col md="6" lg="3" class="mb-3">
                                <b-card class="stat-card shadow-sm border-0 h-100">
                                    <div class="text-muted small text-uppercase font-weight-bold mb-2">Total Expected Capacity</div>
                                    <div class="display-4 font-weight-bold text-primary my-1">
                                        {{ capacityData.totalExpectedHours }}<span class="h4 font-weight-normal text-muted">h</span>
                                    </div>
                                    <div class="small text-muted mt-2">
                                        Across {{ capacityData.members.length }} active team members
                                    </div>
                                </b-card>
                            </b-col>

                            <!-- Logged Hours -->
                            <b-col md="6" lg="3" class="mb-3">
                                <b-card class="stat-card shadow-sm border-0 h-100">
                                    <div class="text-muted small text-uppercase font-weight-bold mb-2">Actual Logged Hours</div>
                                    <div class="display-4 font-weight-bold text-success my-1">
                                        {{ capacityData.totalLoggedHours }}<span class="h4 font-weight-normal text-muted">h</span>
                                    </div>
                                    <div class="d-flex justify-content-between small text-muted mt-2">
                                        <span>Work: <strong>{{ capacityData.totalWorkHours }}h</strong></span>
                                        <span>Activity: <strong>{{ capacityData.totalActivityHours }}h</strong></span>
                                        <span>Absence: <strong>{{ capacityData.totalAbsenceHours }}h</strong></span>
                                    </div>
                                </b-card>
                            </b-col>

                            <!-- Capacity Variance -->
                            <b-col md="6" lg="3" class="mb-3">
                                <b-card class="stat-card shadow-sm border-0 h-100">
                                    <div class="text-muted small text-uppercase font-weight-bold mb-2">Capacity Variance</div>
                                    <div
                                        class="display-4 font-weight-bold my-1"
                                        :class="capacityVariance >= 0 ? 'text-success' : 'text-danger'"
                                    >
                                        {{ capacityVariance >= 0 ? '+' : '' }}{{ capacityVariance }}<span class="h4 font-weight-normal text-muted">h</span>
                                    </div>
                                    <div class="small text-muted mt-2">
                                        {{ capacityVariance >= 0 ? 'Logged ahead of expected pace' : 'Hours remaining to meet capacity' }}
                                    </div>
                                </b-card>
                            </b-col>
                        </b-row>

                        <!-- Member Workload Breakdown Grid -->
                        <div class="mb-4">
                            <h6 class="font-weight-bold text-dark small text-uppercase mb-2">
                                <i class="bi bi-people mr-1"></i>Member Workload Breakdown ({{ capacityData.members.length }})
                            </h6>
                            <grid-shell
                                :columns="capacityMemberGridColumns"
                                :loading="capacityLoading"
                                :empty="!capacityData || !capacityData.members.length"
                                :can-reset="false"
                                empty-title="No Member Capacity Data"
                                empty-message="No capacity records available for this sprint."
                                data-test="table-capacity-members"
                            >
                                <b-card v-for="m in capacityData.members" :key="m.accountUid || m.email" class="my-1 py-1 shadow-none border">
                                    <b-row class="my-1 grid-row align-items-center">
                                        <b-col class="px-2 grid-col-member text-left">
                                            <div class="d-flex align-items-center">
                                                <b-avatar
                                                    :src="m.picture"
                                                    :text="getInitials(m.fullName || m.email)"
                                                    size="38px"
                                                    variant="primary"
                                                    class="mr-2"
                                                ></b-avatar>
                                                <div>
                                                    <div class="d-flex align-items-center flex-wrap">
                                                        <span class="font-weight-bold mr-2">{{ m.fullName || 'Member' }}</span>
                                                        <b-badge v-if="m.isFormerMember" variant="secondary" class="px-2 py-1" data-test="badge-former-member">
                                                            Former Member
                                                        </b-badge>
                                                    </div>
                                                    <div class="small text-muted">{{ m.email }}</div>
                                                </div>
                                            </div>
                                        </b-col>
                                        <b-col class="px-2 grid-col-role text-left">
                                            <b-badge variant="light" class="border px-2 py-1">
                                                {{ m.role || getMemberRole(m) }}
                                            </b-badge>
                                        </b-col>
                                        <b-col class="px-2 grid-col-dailyCapacity text-center">
                                            <div class="mobile-col-caption d-lg-none">Daily Capacity</div>
                                            <span class="badge badge-light border px-2 py-1">{{ m.dailyCapacity }}h / day</span>
                                        </b-col>
                                        <b-col class="px-2 grid-col-expectedHours text-center">
                                            <div class="mobile-col-caption d-lg-none">Target</div>
                                            <strong>{{ m.expectedHours }}h</strong>
                                            <div v-if="m.availableCapacityHours != null && m.availableCapacityHours !== m.expectedHours" class="extra-small text-muted">
                                                Avail: {{ m.availableCapacityHours }}h
                                            </div>
                                        </b-col>
                                        <b-col class="px-2 grid-col-loggedHours text-center">
                                            <div class="mobile-col-caption d-lg-none">Logged</div>
                                            <div class="font-weight-bold">{{ m.loggedHours }}h</div>
                                            <div class="small text-muted">
                                                <span title="Work Hours" class="text-primary">{{ m.workHours }}w</span> &bull;
                                                <span title="Activity Hours" class="text-info">{{ m.activityHours }}a</span> &bull;
                                                <span title="Absence Hours" class="text-secondary">{{ m.absenceHours }}abs</span>
                                            </div>
                                        </b-col>
                                        <b-col class="px-2 grid-col-progress text-center">
                                            <div class="mobile-col-caption d-lg-none">Progress</div>
                                            <div class="d-flex align-items-center" style="min-width: 140px;">
                                                <b-progress
                                                    :value="m.completionPercentage"
                                                    :max="100"
                                                    :variant="getCompletionProgressVariant(m.completionPercentage)"
                                                    height="8px"
                                                    class="flex-grow-1 mr-2 rounded"
                                                ></b-progress>
                                                <span class="small font-weight-bold">{{ m.completionPercentage }}%</span>
                                            </div>
                                        </b-col>
                                        <b-col class="px-2 grid-col-status text-center">
                                            <b-badge :variant="getStatusBadgeVariant(m.status)" class="px-2 py-1">
                                                {{ formatStatus(m.status) }}
                                            </b-badge>
                                        </b-col>
                                    </b-row>
                                </b-card>
                            </grid-shell>
                        </div>

                        <!-- Sprint Burn-Down Pacing Grid -->
                        <div v-if="burnDownData">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="font-weight-bold text-dark small text-uppercase mb-0">
                                    <i class="bi bi-graph-down mr-1"></i>Sprint Burn-Down Pacing
                                </h6>
                                <div class="small text-muted">
                                    Total Target: <strong>{{ burnDownData.totalExpectedHours }}h</strong> | Logged: <strong>{{ burnDownData.totalLoggedHours }}h</strong>
                                </div>
                            </div>
                            <grid-shell
                                :columns="burnDownGridColumns"
                                :loading="capacityLoading"
                                :empty="!burnDownData || !burnDownData.days.length"
                                :can-reset="false"
                                empty-title="No Burn-Down Data"
                                empty-message="No daily burn-down points recorded for this sprint."
                                data-test="grid-burndown-days"
                            >
                                <b-card v-for="d in burnDownData.days" :key="d.date" class="my-1 py-1 shadow-none border">
                                    <b-row class="my-1 grid-row align-items-center">
                                        <b-col class="px-2 grid-col-date text-left" :class="{ 'text-muted': d.isWeekend }">
                                            <span>{{ formatDateString(d.date) }}</span>
                                            <b-badge v-if="d.isWeekend" variant="light" class="ml-1 border">Weekend</b-badge>
                                        </b-col>
                                        <b-col class="px-2 grid-col-dailyLogged text-center">
                                            <div class="mobile-col-caption d-lg-none">Daily Logged</div>
                                            <span v-if="d.dailyLoggedHours > 0" class="badge badge-success px-2 py-1">
                                                +{{ d.dailyLoggedHours }}h
                                            </span>
                                            <span v-else class="text-muted">—</span>
                                        </b-col>
                                        <b-col class="px-2 grid-col-actualCumulative text-center">
                                            <div class="mobile-col-caption d-lg-none">Actual Cumul.</div>
                                            <strong>{{ d.actualCumulativeHours }}h</strong>
                                        </b-col>
                                        <b-col class="px-2 grid-col-expectedCumulative text-center">
                                            <div class="mobile-col-caption d-lg-none">Expected</div>
                                            <span class="text-muted">{{ d.expectedCumulativeHours }}h</span>
                                        </b-col>
                                        <b-col class="px-2 grid-col-variance text-center">
                                            <div class="mobile-col-caption d-lg-none">Variance</div>
                                            <span
                                                :class="d.actualCumulativeHours >= d.expectedCumulativeHours ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'"
                                            >
                                                {{ d.actualCumulativeHours >= d.expectedCumulativeHours ? '+' : '' }}{{ Math.round((d.actualCumulativeHours - d.expectedCumulativeHours) * 10) / 10 }}h
                                            </span>
                                        </b-col>
                                    </b-row>
                                </b-card>
                            </grid-shell>
                        </div>
                    </div>
                </div>
                </b-collapse>
            </b-card>

            <!-- SECTION 2: SPRINT DELIVERABLES & WORKLOAD BALANCE -->
            <b-card no-body class="shadow-sm border mb-4 config-section-card" id="section-team-commitments">
                <div
                    class="card-header border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center section-accordion-header"
                    :class="{ 'border-bottom': isSectionExpanded('commitments'), 'border-bottom-0': !isSectionExpanded('commitments') }"
                    role="button"
                    tabindex="0"
                    :aria-expanded="isSectionExpanded('commitments') ? 'true' : 'false'"
                    aria-controls="collapse-team-commitments"
                    @click="toggleSection('commitments')"
                    @keydown.enter.prevent="toggleSection('commitments')"
                    @keydown.space.prevent="toggleSection('commitments')"
                    data-test="header-team-commitments"
                >
                    <div class="d-flex align-items-center">
                        <span class="section-step-badge mr-3">2</span>
                        <div class="section-icon mr-2 text-success">
                            <i class="bi bi-check2-square fa-lg"></i>
                        </div>
                        <div>
                            <h5 class="font-weight-bold text-dark mb-0 d-flex align-items-center">
                                Sprint Deliverables &amp; Workload Balance
                                <b-badge pill variant="success" class="ml-2 px-2 font-weight-bold" v-if="commitmentsData">{{ commitmentsData.totalItems }}</b-badge>
                            </h5>
                            <div class="small text-muted">
                                Audit sprint commitments, completion ratios, and workload allocation across squad members.
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center mt-2 mt-md-0" @click.stop>
                        <b-button size="sm" variant="outline-primary" @click="loadCommitmentsAsync" :disabled="commitmentsLoading" title="Refresh deliverables" class="mr-2">
                            <b-spinner v-if="commitmentsLoading" small></b-spinner>
                            <span v-else><i class="bi bi-arrow-clockwise mr-1"></i>Refresh Deliverables</span>
                        </b-button>

                        <!-- Accordion Expand/Collapse Indicator -->
                        <div
                            class="accordion-toggle-icon ml-1 pl-3 border-left"
                            data-test="btn-toggle-section-commitments"
                            :title="isSectionExpanded('commitments') ? 'Collapse section' : 'Expand section'"
                            @click="toggleSection('commitments')"
                        >
                            <i class="bi bi-chevron-down accordion-chevron" :class="{ 'is-expanded': isSectionExpanded('commitments') }"></i>
                        </div>
                    </div>
                </div>

                <b-collapse id="collapse-team-commitments" v-model="expandedSections.commitments">

                <div class="card-body p-4 team-commitments-section" data-test="tab-team-commitments">
                    <!-- Attention Signals: blocked work items, unestimated work, silent bottlenecks, assignees on PTO -->
                    <div v-if="hasTeamAttentionSignals" class="mb-4" data-test="team-attention-signals" :data-state="teamAttentionLoading ? 'loading' : 'idle'">
                        <b-alert v-if="teamAttentionData.blockedItems && teamAttentionData.blockedItems.length" show variant="danger" class="mb-2" data-test="alert-team-blocked-tasks">
                            <div class="font-weight-bold mb-1">
                                <i class="bi bi-sign-stop-fill mr-1"></i>Squad blocker radar — {{ teamAttentionData.blockedItems.length }} blocked work item(s) in this sprint
                            </div>
                            <div v-for="i in teamAttentionData.blockedItems" :key="'blocked-' + i.id" class="small">
                                <a :href="i.url" target="_blank" rel="noopener noreferrer">#{{ i.id }} {{ i.title }}</a>
                                — {{ i.assignedToName }} ({{ i.state }})<span v-if="i.blockedReason"> &bull; <strong>{{ i.blockedReason }}</strong></span>
                            </div>
                        </b-alert>
                        <b-alert v-if="unestimatedGroups.length" show variant="warning" class="mb-2" data-test="alert-team-unestimated-tasks">
                            <div class="font-weight-bold mb-1">
                                <i class="bi bi-rulers mr-1"></i>Unestimated assigned sprint work — {{ teamAttentionData.unestimatedItems.length }} item(s) across {{ unestimatedGroups.length }} member(s)
                            </div>
                            <div v-for="g in unestimatedGroups" :key="'unest-' + g.name" class="small">
                                <strong>{{ g.name }}</strong>:
                                <a v-for="i in g.items" :key="i.id" :href="i.url" target="_blank" rel="noopener noreferrer" class="mr-2">#{{ i.id }} {{ i.title }}</a>
                            </div>
                        </b-alert>
                        <b-alert v-if="teamAttentionData.silentBottlenecks.length" show variant="danger" class="mb-2" data-test="alert-team-silent-bottleneck">
                            <div class="font-weight-bold mb-1">
                                <i class="bi bi-hourglass-split mr-1"></i>Silent bottlenecks — {{ teamAttentionData.silentBottlenecks.length }} task(s) In Progress for more than 4 working days with 0h logged and no PR or comment activity
                            </div>
                            <div v-for="i in teamAttentionData.silentBottlenecks" :key="'silent-' + i.id" class="small">
                                <a :href="i.url" target="_blank" rel="noopener noreferrer">#{{ i.id }} {{ i.title }}</a>
                                — {{ i.assignedToName }}, {{ i.daysInState }} working days in {{ i.state }}
                            </div>
                        </b-alert>
                        <b-alert v-if="assigneesOnPto.length" show variant="info" class="mb-2" data-test="alert-team-assignee-on-pto">
                            <div class="font-weight-bold mb-1">
                                <i class="bi bi-airplane mr-1"></i>Assignees on planned PTO with open sprint work
                            </div>
                            <div v-for="a in assigneesOnPto" :key="'pto-' + a.name" class="small d-flex align-items-center flex-wrap">
                                <b-badge variant="warning" class="mr-2" data-test="badge-assignee-on-pto" :data-test-id="a.name" v-b-tooltip.hover.top="a.pto.tooltip">
                                    <i class="bi bi-airplane mr-1"></i>{{ a.name }} · {{ a.pto.label }}
                                </b-badge>
                                <span>{{ a.openItemCount }} open item(s), {{ a.remainingHours }}h remaining — reassign before the sprint stalls.</span>
                            </div>
                        </b-alert>
                    </div>
                    <!-- Commitments KPI Summary Strip -->
                    <b-row class="mb-4" v-if="commitmentsData">
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Committed Items</div>
                                <div class="display-4 font-weight-bold text-dark my-1">{{ commitmentsData.totalItems }}</div>
                                <div class="small text-muted">{{ commitmentsData.activeItems }} active &bull; {{ commitmentsData.completedItems }} completed</div>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Total Estimated</div>
                                <div class="display-4 font-weight-bold text-primary my-1">{{ commitmentsData.totalEstimatedHours }}<span class="h4 font-weight-normal text-muted">h</span></div>
                                <div class="small text-muted">Committed work items estimate</div>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Completed Effort</div>
                                <div class="display-4 font-weight-bold text-success my-1">{{ commitmentsData.totalCompletedHours }}<span class="h4 font-weight-normal text-muted">h</span></div>
                                <div class="small text-muted">Remaining: <strong>{{ commitmentsData.totalRemainingHours }}h</strong></div>
                            </b-card>
                        </b-col>
                        <b-col md="3" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Deliverable Completion</div>
                                <div class="display-4 font-weight-bold text-info my-1">{{ commitmentsData.completionPercentage }}%</div>
                                <b-progress :value="commitmentsData.completionPercentage" :max="100" variant="success" height="6px" class="rounded"></b-progress>
                            </b-card>
                        </b-col>
                    </b-row>

                    <!-- Squad Workload vs. Available Capacity Balance -->
                    <div v-if="workloadBalanceItems.length" class="mb-4" data-test="section-workload-balance">
                        <h6 class="font-weight-bold text-dark small text-uppercase mb-2">
                            <i class="bi bi-sliders2-vertical text-primary mr-1"></i>Squad Workload vs. Available Capacity Balance
                        </h6>
                        <grid-shell
                            :columns="workloadBalanceGridColumns"
                            :empty="!workloadBalanceItems.length"
                            :can-reset="false"
                            empty-title="No Workload Balance Data"
                            empty-message="No member allocation balance data available."
                            data-test="table-workload-balance"
                        >
                            <b-card v-for="w in workloadBalanceItems" :key="w.accountUid || w.email" class="my-1 py-1 shadow-none border">
                                <b-row class="my-1 grid-row align-items-center">
                                    <b-col class="px-2 grid-col-member text-left">
                                        <div class="d-flex align-items-center">
                                            <b-avatar
                                                :src="w.picture"
                                                :text="getInitials(w.fullName || w.email)"
                                                size="30px"
                                                variant="primary"
                                                class="mr-2"
                                            ></b-avatar>
                                            <div>
                                                <div class="font-weight-bold small">{{ w.fullName }}</div>
                                                <div class="extra-small text-muted">{{ w.email }}</div>
                                            </div>
                                        </div>
                                    </b-col>
                                    <b-col class="px-2 grid-col-availableCapacityHours text-center">
                                        <div class="mobile-col-caption d-lg-none">Avail. Capacity</div>
                                        <span class="font-weight-bold">{{ w.availableCapacityHours }}h</span>
                                    </b-col>
                                    <b-col class="px-2 grid-col-assignedEstimatedHours text-center">
                                        <div class="mobile-col-caption d-lg-none">Estimated</div>
                                        <span>{{ w.assignedEstimatedHours }}h</span>
                                    </b-col>
                                    <b-col class="px-2 grid-col-assignedRemainingHours text-center">
                                        <div class="mobile-col-caption d-lg-none">Remaining</div>
                                        <span class="font-weight-bold text-primary">{{ w.assignedRemainingHours }}h</span>
                                    </b-col>
                                    <b-col class="px-2 grid-col-activeDeliverablesCount text-center">
                                        <div class="mobile-col-caption d-lg-none">Deliverables</div>
                                        <b-badge variant="light" class="border px-2 py-1">{{ w.activeDeliverablesCount }}</b-badge>
                                    </b-col>
                                    <b-col class="px-2 grid-col-allocation text-center">
                                        <div class="mobile-col-caption d-lg-none">Load Ratio</div>
                                        <div class="d-flex align-items-center" style="min-width: 130px;">
                                            <b-progress
                                                :value="Math.min(100, w.loadRatioPercentage || 0)"
                                                :max="100"
                                                :variant="getLoadStatusVariant(w.loadStatus)"
                                                height="7px"
                                                class="flex-grow-1 mr-2 rounded"
                                            ></b-progress>
                                            <span class="small font-weight-bold">{{ w.loadRatioPercentage }}%</span>
                                        </div>
                                    </b-col>
                                    <b-col class="px-2 grid-col-loadStatus text-center">
                                        <b-badge :variant="getLoadStatusVariant(w.loadStatus)" class="px-2 py-1" data-test="badge-load-status">
                                            {{ formatLoadStatus(w.loadStatus) }}
                                        </b-badge>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </grid-shell>
                    </div>

                    <!-- Deliverables Grid -->
                    <h6 class="font-weight-bold text-dark small text-uppercase mb-2">
                        <i class="bi bi-kanban mr-1"></i>Sprint Deliverables
                    </h6>
                    <grid-shell
                        :columns="commitmentGridColumns"
                        :loading="commitmentsLoading"
                        :empty="!commitmentsLoading && !filteredCommitments.length"
                        :has-active-filters="isCommitmentFilterActive"
                        @reset-filters="resetCommitmentFilters"
                        empty-title="No Deliverables Found"
                        empty-message="No sprint work items match your filter criteria."
                        data-test="grid-team-commitments"
                    >
                        <template #view>
                            <div v-if="false" class="filter-toolbar-label mr-2 flex-shrink-0">
                                <i class="bi bi-funnel mr-1"></i><strong>View:</strong>
                            </div>
                            <span class="small font-weight-bold text-muted text-uppercase mr-2">Filter State:</span>
                            <b-button-group size="sm" class="mr-3 mb-2 mb-sm-0">
                                <b-button :variant="commitmentStateFilter === 'All' ? 'primary' : 'outline-secondary'" @click="commitmentStateFilter = 'All'">All</b-button>
                                <b-button :variant="commitmentStateFilter === 'Active' ? 'primary' : 'outline-secondary'" @click="commitmentStateFilter = 'Active'">In Progress / Active</b-button>
                                <b-button :variant="commitmentStateFilter === 'Done' ? 'primary' : 'outline-secondary'" @click="commitmentStateFilter = 'Done'">Completed</b-button>
                            </b-button-group>

                            <span class="small font-weight-bold text-muted text-uppercase mr-2">Assignee:</span>
                            <b-form-select v-model="commitmentMemberFilter" size="sm" style="width: 180px;" class="mb-2 mb-sm-0" data-test="select-commitment-assignee">
                                <b-form-select-option value="All">All Squad Members</b-form-select-option>
                                <b-form-select-option v-for="m in uniqueCommitmentMembers" :key="m" :value="m">{{ m }}</b-form-select-option>
                            </b-form-select>

                            <div class="d-flex align-items-center">
                                <b-button size="sm" variant="outline-primary" @click="loadCommitmentsAsync" :disabled="commitmentsLoading">
                                    <b-spinner v-if="commitmentsLoading" small></b-spinner>
                                    <span v-else><i class="bi bi-arrow-clockwise mr-1"></i>Refresh Deliverables</span>
                                </b-button>
                            </div>
                        </template>

                        <b-card v-for="c in filteredCommitments" :key="c.workItemId" class="my-1 py-1 shadow-none border">
                            <b-row class="my-1 grid-row align-items-center">
                                <b-col class="px-2 grid-col-workItemId text-center">
                                    <b-badge variant="light" class="border px-2 py-1 font-weight-bold">
                                        #{{ c.workItemId }}
                                    </b-badge>
                                </b-col>
                                <b-col class="px-2 grid-col-item text-left">
                                    <div class="d-flex align-items-center">
                                        <i :class="getWorkItemTypeIcon(c.workItemType)" class="mr-2 h5 mb-0" :title="c.workItemType"></i>
                                        <div>
                                            <div class="font-weight-bold">{{ c.title }}</div>
                                            <div class="small text-muted" v-if="c.sprintName">{{ c.sprintName }}</div>
                                        </div>
                                    </div>
                                </b-col>
                                <b-col class="px-2 grid-col-assignedTo text-left">
                                    <div class="d-flex align-items-center">
                                        <b-avatar
                                            :src="c.assignedToPicture"
                                            :text="getInitials(c.assignedToName)"
                                            size="28px"
                                            variant="secondary"
                                            class="mr-2"
                                        ></b-avatar>
                                        <span class="small font-weight-bold">{{ c.assignedToName }}</span>
                                    </div>
                                </b-col>
                                <b-col class="px-2 grid-col-state text-center">
                                    <status-badge :status="c.state" />
                                </b-col>
                                <b-col class="px-2 grid-col-estimate text-center">
                                    <div class="mobile-col-caption d-lg-none">Estimate</div>
                                    <span class="small font-weight-bold">{{ c.originalEstimateHours }}h</span>
                                </b-col>
                                <b-col class="px-2 grid-col-completed text-center">
                                    <div class="mobile-col-caption d-lg-none">Completed</div>
                                    <span class="small text-success font-weight-bold">{{ c.completedHours }}h</span>
                                </b-col>
                                <b-col class="px-2 grid-col-remaining text-center">
                                    <div class="mobile-col-caption d-lg-none">Remaining</div>
                                    <span class="small text-muted font-weight-bold">{{ c.remainingHours }}h</span>
                                </b-col>
                                <b-col class="px-2 grid-col-progress text-center">
                                    <div class="mobile-col-caption d-lg-none">Progress</div>
                                    <div class="d-flex align-items-center" style="min-width: 100px;">
                                        <b-progress
                                            :value="c.completionPercentage"
                                            :max="100"
                                            :variant="c.completionPercentage === 100 ? 'success' : 'primary'"
                                            height="6px"
                                            class="flex-grow-1 mr-2 rounded"
                                        ></b-progress>
                                        <span class="small font-weight-bold">{{ c.completionPercentage }}%</span>
                                    </div>
                                </b-col>
                            </b-row>
                        </b-card>
                    </grid-shell>
                </div>
                </b-collapse>
            </b-card>

            <!-- SECTION 3: ABSENCE SCHEDULE -->
            <b-card no-body class="shadow-sm border mb-4 config-section-card" id="section-team-absences">
                <div
                    class="card-header border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center section-accordion-header"
                    :class="{ 'border-bottom': isSectionExpanded('absences'), 'border-bottom-0': !isSectionExpanded('absences') }"
                    role="button"
                    tabindex="0"
                    :aria-expanded="isSectionExpanded('absences') ? 'true' : 'false'"
                    aria-controls="collapse-team-absences"
                    @click="toggleSection('absences')"
                    @keydown.enter.prevent="toggleSection('absences')"
                    @keydown.space.prevent="toggleSection('absences')"
                    data-test="header-team-absences"
                >
                    <div class="d-flex align-items-center">
                        <span class="section-step-badge mr-3">3</span>
                        <div class="section-icon mr-2 text-warning">
                            <i class="bi bi-calendar-event fa-lg"></i>
                        </div>
                        <div>
                            <h5 class="font-weight-bold text-dark mb-0 d-flex align-items-center">
                                Absence Schedule
                                <b-badge pill variant="warning" class="ml-2 px-2 font-weight-bold" v-if="absencesData">{{ absencesData.absences.length }}</b-badge>
                            </h5>
                            <div class="small text-muted">
                                Coordinate team PTO, sick leave, and regional public holidays in a unified availability timeline.
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center mt-2 mt-md-0" @click.stop>
                        <b-button size="sm" variant="outline-primary" @click="loadAbsencesAsync" :disabled="absencesLoading" class="mr-2">
                            <b-spinner v-if="absencesLoading" small></b-spinner>
                            <span v-else><i class="bi bi-arrow-clockwise mr-1"></i>Refresh Leave</span>
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" to="/timelog" title="Record your own absence in Timelog">
                            <i class="bi bi-box-arrow-up-right mr-1"></i>Log Absence
                        </b-button>

                        <!-- Accordion Expand/Collapse Indicator -->
                        <div
                            class="accordion-toggle-icon ml-3 pl-3 border-left"
                            data-test="btn-toggle-section-absences"
                            :title="isSectionExpanded('absences') ? 'Collapse section' : 'Expand section'"
                            @click="toggleSection('absences')"
                        >
                            <i class="bi bi-chevron-down accordion-chevron" :class="{ 'is-expanded': isSectionExpanded('absences') }"></i>
                        </div>
                    </div>
                </div>

                <b-collapse id="collapse-team-absences" v-model="expandedSections.absences">

                <div class="card-body p-4 team-absences-section" data-test="tab-team-absences">
                    <!-- Absence KPI Summary Strip -->
                    <b-row class="mb-4" v-if="absencesData">
                        <b-col md="4" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Scheduled Absences</div>
                                <div class="display-4 font-weight-bold text-dark my-1">{{ absencesData.absences.length }}</div>
                                <div class="small text-muted">Across {{ absencesData.membersWithAbsenceCount }} squad members</div>
                            </b-card>
                        </b-col>
                        <b-col md="4" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Total Days Out</div>
                                <div class="display-4 font-weight-bold text-warning my-1">{{ absencesData.totalAbsenceDays }}<span class="h4 font-weight-normal text-muted">d</span></div>
                                <div class="small text-muted">Planned time off &amp; statutory leave</div>
                            </b-card>
                        </b-col>
                        <b-col md="4" sm="6" class="mb-3 mb-md-0">
                            <b-card class="stat-card shadow-sm border-0 h-100">
                                <div class="text-muted small text-uppercase font-weight-bold">Total Absence Hours</div>
                                <div class="display-4 font-weight-bold text-danger my-1">{{ absencesData.totalAbsenceHours }}<span class="h4 font-weight-normal text-muted">h</span></div>
                                <div class="small text-muted">Excluded from sprint capacity target</div>
                            </b-card>
                        </b-col>
                    </b-row>

                    <!-- Absences Grid -->
                    <grid-shell
                        :columns="absenceGridColumns"
                        :loading="absencesLoading"
                        :empty="!absencesLoading && !filteredAbsences.length"
                        :has-active-filters="absenceTypeFilter !== 'All'"
                        @reset-filters="absenceTypeFilter = 'All'"
                        empty-title="No Absences Scheduled"
                        empty-message="No absence records match your filter criteria."
                        data-test="grid-team-absences"
                    >
                        <template #view>
                            <div v-if="false" class="filter-toolbar-label mr-2 flex-shrink-0">
                                <i class="bi bi-funnel mr-1"></i><strong>View:</strong>
                            </div>
                            <span class="small font-weight-bold text-muted text-uppercase mr-2">Category:</span>
                            <b-button-group size="sm" class="mr-2 mb-2 mb-sm-0">
                                <b-button :variant="absenceTypeFilter === 'All' ? 'primary' : 'outline-secondary'" @click="absenceTypeFilter = 'All'">All</b-button>
                                <b-button :variant="absenceTypeFilter === 'Vacation' ? 'primary' : 'outline-secondary'" @click="absenceTypeFilter = 'Vacation'">Vacation / PTO</b-button>
                                <b-button :variant="absenceTypeFilter === 'Sick' ? 'primary' : 'outline-secondary'" @click="absenceTypeFilter = 'Sick'">Sick Leave</b-button>
                                <b-button :variant="absenceTypeFilter === 'Holiday' ? 'primary' : 'outline-secondary'" @click="absenceTypeFilter = 'Holiday'">Public Holiday</b-button>
                                <b-button :variant="absenceTypeFilter === 'Training' ? 'primary' : 'outline-secondary'" @click="absenceTypeFilter = 'Training'">Training</b-button>
                            </b-button-group>
                        </template>

                        <b-card v-for="a in filteredAbsences" :key="a.id || (a.memberEmail + a.startDate)" class="my-1 py-1 shadow-none border">
                            <b-row class="my-1 grid-row align-items-center">
                                <b-col class="px-2 grid-col-member text-left">
                                    <div class="d-flex align-items-center">
                                        <b-avatar
                                            :src="a.memberPicture"
                                            :text="getInitials(a.memberName)"
                                            size="34px"
                                            variant="secondary"
                                            class="mr-2"
                                        ></b-avatar>
                                        <div>
                                            <div class="font-weight-bold">{{ a.memberName }}</div>
                                            <div class="small text-muted">{{ a.memberEmail }}</div>
                                        </div>
                                    </div>
                                </b-col>
                                <b-col class="px-2 grid-col-type text-center">
                                    <b-badge :variant="getAbsenceBadgeVariant(a.absenceType)" class="px-2 py-1">
                                        {{ a.absenceType }}
                                    </b-badge>
                                </b-col>
                                <b-col class="px-2 grid-col-dates text-center">
                                    <div class="mobile-col-caption d-lg-none">Dates</div>
                                    <span class="small font-weight-bold">{{ formatDateString(a.startDate) }}</span>
                                    <span v-if="formatDateString(a.startDate) !== formatDateString(a.endDate)" class="small text-muted">
                                        — {{ formatDateString(a.endDate) }}
                                    </span>
                                </b-col>
                                <b-col class="px-2 grid-col-duration text-center">
                                    <div class="mobile-col-caption d-lg-none">Duration</div>
                                    <span class="badge badge-light border px-2 py-1">
                                        {{ a.days }}d ({{ a.hours }}h)
                                    </span>
                                </b-col>
                                <b-col class="px-2 grid-col-status text-center">
                                    <b-badge variant="success" class="px-2 py-1">
                                        <i class="bi bi-check mr-1"></i>{{ a.status || 'Approved' }}
                                    </b-badge>
                                </b-col>
                                <b-col class="px-2 grid-col-notes text-left">
                                    <div class="mobile-col-caption d-lg-none">Notes</div>
                                    <span class="small text-muted">{{ a.notes || '—' }}</span>
                                </b-col>
                            </b-row>
                        </b-card>
                    </grid-shell>
                </div>
                </b-collapse>
            </b-card>


        </b-container>
    </div>
</template>

<script>
    import accountMixin from "../mixinAccount";
    import mixinToast from "../mixinToast";
    import { computePersonPto } from "../mixinPtoSignals";
    import PickerSprints from "./picker-sprints.vue";
    import StatusBadge from "./status-badge.vue";
    import CardMissingSource from "./card-missing-source.vue";
    import GridShell from "./grid-shell.vue";
    import eventBus from "../event-bus";
    import axios from "axios";

    export default {
        name: "PageTeamDashboard",
        mixins: [accountMixin, mixinToast],
        components: {
            "picker-sprints": PickerSprints,
            "status-badge": StatusBadge,
            "card-missing-source": CardMissingSource,
            "grid-shell": GridShell
        },
        data() {
            const now = new Date();
            const firstDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
            const lastDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);

            return {
                activeTab: 0,
                activeSection: "capacity",
                expandedSections: {
                    capacity: true,
                    commitments: false,
                    absences: false
                },
                loadingAll: false,
                sprintDataAbortController: null,

                // Team & Project Space Filter Selection
                selectedWorkspaceId: null,
                selectedProjectSpaceId: null,

                // Capacity Tab Data
                startDate: firstDay,
                endDate: lastDay,
                preset: "thisMonth",
                capacityData: null,
                burnDownData: null,
                capacityLoading: true,

                // Commitments Tab Data
                commitmentsData: null,
                commitmentsLoading: true,
                commitmentStateFilter: "All",
                commitmentMemberFilter: "All",

                // Absences Tab Data
                absencesData: null,
                absencesLoading: true,
                absenceTypeFilter: "All",
                teamAttentionData: null,
                teamAttentionLoading: false,

                // GridShell Column Definitions
                capacityMemberGridColumns: [
                    { key: "member", label: "Team Member", width: "minmax(200px, 2fr)", headerClass: "text-left" },
                    { key: "role", label: "Role / Title", width: "minmax(140px, 1.2fr)", headerClass: "text-left" },
                    { key: "dailyCapacity", label: "Capacity", width: "110px", headerClass: "text-center" },
                    { key: "expectedHours", label: "Expected / Avail", width: "120px", headerClass: "text-center" },
                    { key: "loggedHours", label: "Logged Hours", width: "120px", headerClass: "text-center" },
                    { key: "progress", label: "Completion", width: "minmax(140px, 1.2fr)", headerClass: "text-center" },
                    { key: "status", label: "Status", width: "120px", headerClass: "text-center" }
                ],
                burnDownGridColumns: [
                    { key: "date", label: "Date", width: "minmax(160px, 1.5fr)", headerClass: "text-left" },
                    { key: "dailyLogged", label: "Day Logged", width: "130px", headerClass: "text-center" },
                    { key: "actualCumulative", label: "Cumulative Logged", width: "150px", headerClass: "text-center" },
                    { key: "expectedCumulative", label: "Expected Linear", width: "150px", headerClass: "text-center" },
                    { key: "variance", label: "Variance", width: "130px", headerClass: "text-center" }
                ],
                workloadBalanceGridColumns: [
                    { key: "member", label: "Squad Member", width: "minmax(200px, 2fr)", headerClass: "text-left" },
                    { key: "availableCapacityHours", label: "Available (After PTO)", width: "130px", headerClass: "text-center" },
                    { key: "assignedEstimatedHours", label: "Assigned Est.", width: "120px", headerClass: "text-center" },
                    { key: "assignedRemainingHours", label: "Remaining Work", width: "120px", headerClass: "text-center" },
                    { key: "activeDeliverablesCount", label: "Active Items", width: "100px", headerClass: "text-center" },
                    { key: "allocation", label: "Capacity Load", width: "minmax(140px, 1.2fr)", headerClass: "text-center" },
                    { key: "loadStatus", label: "Balance Status", width: "140px", headerClass: "text-center" }
                ],
                commitmentGridColumns: [
                    { key: "workItemId", label: "ID", width: "80px", headerClass: "text-center" },
                    { key: "item", label: "Deliverable", width: "minmax(220px, 2.5fr)", headerClass: "text-left" },
                    { key: "assignedTo", label: "Assigned To", width: "minmax(140px, 1.2fr)", headerClass: "text-left" },
                    { key: "state", label: "State", width: "110px", headerClass: "text-center" },
                    { key: "estimate", label: "Estimate", width: "90px", headerClass: "text-center" },
                    { key: "completed", label: "Completed", width: "90px", headerClass: "text-center" },
                    { key: "remaining", label: "Remaining", width: "90px", headerClass: "text-center" },
                    { key: "progress", label: "Progress", width: "120px", headerClass: "text-center" }
                ],
                absenceGridColumns: [
                    { key: "member", label: "Team Member", width: "minmax(200px, 2fr)", headerClass: "text-left" },
                    { key: "type", label: "Absence Type", width: "140px", headerClass: "text-center" },
                    { key: "dates", label: "Date / Period", width: "180px", headerClass: "text-center" },
                    { key: "duration", label: "Duration", width: "110px", headerClass: "text-center" },
                    { key: "status", label: "Status", width: "110px", headerClass: "text-center" },
                    { key: "notes", label: "Notes & Context", width: "minmax(160px, 1.5fr)", headerClass: "text-left" }
                ]
            };
        },
        computed: {
            workspace() {
                return this.$store.state.workspace;
            },
            availableWorkspaces() {
                const list = (this.workspaces || []).filter(
                    w => w && w.isTeamSpace !== false && (w.inviteToken || w.isTeamSpace === true)
                );
                if (list.length) return list;
                if (this.workspace && this.workspace.isTeamSpace !== false && (this.workspace.inviteToken || this.workspace.isTeamSpace === true)) {
                    return [this.workspace];
                }
                return [];
            },
            activeTeamObj() {
                if (this.selectedWorkspaceId) {
                    const found = this.availableWorkspaces.find(w => w.workspaceId === this.selectedWorkspaceId);
                    if (found) return found;
                }
                return this.availableWorkspaces[0] || null;
            },
            activeTeamProjectSpaces() {
                const team = this.activeTeamObj;
                if (!team) return [];
                return team.linkedProjectSpaces || team.sharedProjectSpaces || [];
            },
            isCurrentTeamAdmin() {
                const role = this.activeTeamObj?.currentUserRole;
                if (role !== undefined && role !== null) {
                    return role === 0 || role === "0" || role === "Owner" || role === 1 || role === "1" || role === "Admin";
                }
                if (this.capacityData && typeof this.capacityData.isAdminView === "boolean") {
                    return this.capacityData.isAdminView;
                }
                return false;
            },
            currentTeamRoleLabel() {
                return this.formatTeamRole(this.activeTeamObj?.currentUserRole);
            },
            unestimatedGroups() {
                const items = (this.teamAttentionData && this.teamAttentionData.unestimatedItems) || [];
                const map = new Map();
                items.forEach(i => {
                    if (!map.has(i.assignedToName)) map.set(i.assignedToName, { name: i.assignedToName, items: [] });
                    map.get(i.assignedToName).items.push(i);
                });
                return [...map.values()];
            },
            assigneesOnPto() {
                const data = this.teamAttentionData;
                const absences = (this.absencesData && this.absencesData.absences) || [];
                if (!data || !Array.isArray(data.openWorkByAssignee) || !absences.length) return [];
                const today = new Date();
                const todayIso = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())).toISOString().slice(0, 10);
                const toIso = this.endDate ? String(this.endDate).slice(0, 10) : '';
                return data.openWorkByAssignee
                    .map(w => ({ ...w, pto: computePersonPto(absences, w.name, w.email, todayIso, toIso) }))
                    .filter(w => w.pto && w.openItemCount > 0);
            },
            hasTeamAttentionSignals() {
                return !!this.teamAttentionData && (
                    (this.teamAttentionData.blockedItems && this.teamAttentionData.blockedItems.length > 0)
                    || this.unestimatedGroups.length > 0
                    || this.teamAttentionData.silentBottlenecks.length > 0
                    || this.assigneesOnPto.length > 0
                );
            },
            workloadBalanceItems() {
                if (this.commitmentsData?.workloadBalance && this.commitmentsData.workloadBalance.length) {
                    return this.commitmentsData.workloadBalance;
                }
                return [];
            },
            workspaceName() {
                return this.activeTeamObj?.name || this.workspace?.name || "Workspace";
            },
            capacityVariance() {
                if (!this.capacityData) return 0;
                return Math.round((this.capacityData.totalLoggedHours - this.capacityData.totalExpectedHours) * 10) / 10;
            },
            uniqueCommitmentMembers() {
                if (!this.commitmentsData?.items) return [];
                const members = new Set();
                this.commitmentsData.items.forEach(i => {
                    if (i.assignedToName) members.add(i.assignedToName);
                });
                return Array.from(members);
            },
            filteredCommitments() {
                if (!this.commitmentsData?.items) return [];
                let items = this.commitmentsData.items;

                if (this.commitmentStateFilter === "Active") {
                    items = items.filter(i => !["Done", "Closed"].includes(i.state));
                } else if (this.commitmentStateFilter === "Done") {
                    items = items.filter(i => ["Done", "Closed"].includes(i.state));
                }

                if (this.commitmentMemberFilter !== "All") {
                    items = items.filter(i => i.assignedToName === this.commitmentMemberFilter);
                }

                return items;
            },
            filteredAbsences() {
                if (!this.absencesData?.absences) return [];
                let items = this.absencesData.absences;

                if (this.absenceTypeFilter !== "All") {
                    items = items.filter(a => (a.absenceType || "").toLowerCase().includes(this.absenceTypeFilter.toLowerCase()));
                }

                return items;
            },
            isCommitmentFilterActive() {
                return this.commitmentStateFilter !== "All" || this.commitmentMemberFilter !== "All";
            },
            isServiceUnavailable() {
                return !!this.$store.state.isServiceUnavailable;
            }
        },
        watch: {
            async isDevEnv(newVal) {
                if (newVal) {
                    this.loadingAll = true;
                    await this.loadTeamSessionDataAsync();
                    if (!this.selectedWorkspaceId && this.availableWorkspaces.length) {
                        this.selectedWorkspaceId = this.availableWorkspaces[0].workspaceId;
                    }
                    await this.loadAllDataAsync();
                }
            },
            async isTeamOrHigher() {
                await this.loadAllDataAsync();
            },
            "$route.query.tab": {
                immediate: true,
                handler(tab) {
                    this.syncTabFromQuery(tab);
                }
            },
            "$route.query.section": {
                immediate: true,
                handler(sec) {
                    if (sec) {
                        const secMap = {
                            profiles: "profiles",
                            capacity: "capacity",
                            commitments: "commitments",
                            absences: "absences"
                        };
                        this.openAndScrollToSection(secMap[sec] || sec);
                    }
                }
            },
            "$route.query.workspaceId"(wsId) {
                if (wsId && wsId !== this.selectedWorkspaceId) {
                    this.selectedWorkspaceId = wsId;
                    this.selectedProjectSpaceId = null;
                    this.loadAllDataAsync();
                }
            }
        },
        async created() {
            if (!this.isDevEnv) {
                this.loadingAll = false;
                this.loadingSubordinates = false;
                this.capacityLoading = false;
                this.commitmentsLoading = false;
                this.absencesLoading = false;
                return;
            }
            this.syncTabFromQuery(this.$route.query.tab);
            this.loadingAll = true;
            await this.loadTeamSessionDataAsync();
            const queryWsId = this.$route.query.workspaceId;
            if (queryWsId && this.availableWorkspaces.some(w => w.workspaceId === queryWsId)) {
                this.selectedWorkspaceId = queryWsId;
            } else if (this.availableWorkspaces.length) {
                this.selectedWorkspaceId = this.availableWorkspaces[0].workspaceId;
            } else {
                this.selectedWorkspaceId = null;
            }
            await this.loadAllDataAsync();
        },
        beforeUnmount() {
            if (this.sprintDataAbortController) {
                this.sprintDataAbortController.abort();
                this.sprintDataAbortController = null;
            }
            if (this.onServiceReconnected) {
                eventBus.$off('service-reconnected', this.onServiceReconnected);
            }
        },
        methods: {
            formatTeamRole(role) {
                if (role === 0 || role === "0" || role === "Owner") return "Owner";
                if (role === 1 || role === "1" || role === "Admin") return "Admin";
                if (role === 2 || role === "2" || role === "Member") return "Member";
                if (role === 3 || role === "3" || role === "ReadOnly" || role === "Guest") return "Read-Only";
                return "Member";
            },
            onTeamSelectionChange() {
                this.selectedProjectSpaceId = null;
                if (this.activeTeamObj) {
                    this.$store.commit("setWorkspace", this.activeTeamObj);
                }
                this.loadAllDataAsync();
            },
            onProjectSpaceSelectionChange() {
                this.loadAllDataAsync();
            },
            goToManageTeams() {
                this.$router.push({
                    path: "/config",
                    query: { section: "teams" }
                }).catch(() => {});
            },

            getLoadStatusVariant(status) {
                if (status === "Overloaded") return "danger";
                if (status === "HasSpareCapacity" || status === "UnderAllocated") return "info";
                return "success";
            },
            formatLoadStatus(status) {
                if (status === "Overloaded") return "Overloaded";
                if (status === "HasSpareCapacity" || status === "UnderAllocated") return "Spare Capacity";
                return "Balanced";
            },
            scrollToSection(sectionId) {
                if (!sectionId) return;
                const reverseMap = {
                    "section-team-capacity": "capacity",
                    "section-team-commitments": "commitments",
                    "section-team-absences": "absences"
                };
                const key = reverseMap[sectionId];
                if (key) {
                    this.activeSection = key;
                    this.expandedSections = {
                        capacity: key === "capacity",
                        commitments: key === "commitments",
                        absences: key === "absences"
                    };
                }
                this.$nextTick(() => {
                    const el = document.getElementById(sectionId);
                    if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                });
            },
            openAndScrollToSection(key) {
                this.activeSection = key;
                this.expandedSections = {
                    capacity: key === "capacity",
                    commitments: key === "commitments",
                    absences: key === "absences"
                };
                const secMap = {
                    capacity: "section-team-capacity",
                    commitments: "section-team-commitments",
                    absences: "section-team-absences"
                };
                const sectionId = secMap[key] || key;
                this.scrollToSection(sectionId);
            },
            isSectionExpanded(key) {
                return this.activeSection === key;
            },
            toggleSection(key) {
                if (this.activeSection === key) {
                    this.activeSection = null;
                } else {
                    this.activeSection = key;
                }
                this.expandedSections = {
                    capacity: this.activeSection === "capacity",
                    commitments: this.activeSection === "commitments",
                    absences: this.activeSection === "absences"
                };
                if (this.activeSection && this.$route.query.tab !== this.activeSection) {
                    this.$router.replace({
                        query: { ...this.$route.query, tab: this.activeSection }
                    }).catch(() => {});
                }
            },
            expandAllSections() {
                this.activeSection = "all";
                this.expandedSections = {
                    capacity: true,
                    commitments: true,
                    absences: true
                };
            },
            collapseAllSections() {
                this.activeSection = null;
                this.expandedSections = {
                    capacity: false,
                    commitments: false,
                    absences: false
                };
            },
            resetCommitmentFilters() {
                this.commitmentStateFilter = "All";
                this.commitmentMemberFilter = "All";
            },
            syncTabFromQuery(tab) {
                if (tab === "commitments") {
                    this.activeTab = 1;
                    this.openAndScrollToSection("commitments");
                } else if (tab === "absences") {
                    this.activeTab = 2;
                    this.openAndScrollToSection("absences");
                } else if (tab === "profiles") {
                    this.$router.replace({ path: '/team-insights' }).catch(() => {});
                } else {
                    this.activeTab = 0;
                    this.openAndScrollToSection("capacity");
                }
            },
            handleTabChange(tabIndex) {
                const tabMap = ["capacity", "commitments", "absences"];
                const targetTab = tabMap[tabIndex] || "capacity";
                if (this.$route.query.tab !== targetTab) {
                    this.$router.replace({
                        query: { ...this.$route.query, tab: targetTab }
                    }).catch(() => {});
                }
            },
            openUpgradeModal() {
                this.$router.push({
                    path: "/plans",
                    query: {
                        reason: "Unlock Team & Capacity Management suite",
                        requiredPlan: "Team"
                    }
                }).catch(() => {});
            },
            async loadAllDataAsync() {
                if (!this.availableWorkspaces.length) {
                    this.capacityLoading = false;
                    this.commitmentsLoading = false;
                    this.absencesLoading = false;
                    this.loadingAll = false;
                    return;
                }
                this.loadingAll = true;
                await Promise.all([
                    this.loadCapacityReportsAsync(),
                    this.loadCommitmentsAsync(),
                    this.loadAbsencesAsync()
                ]);
                this.loadingAll = false;
            },
            async loadCapacityReportsAsync(controller = null) {
                if (!this.isTeamOrHigher) {
                    this.capacityData = this.getMockCapacityData();
                    this.burnDownData = this.getMockBurnDownData();
                    this.capacityLoading = false;
                    return;
                }
                this.capacityLoading = true;
                try {
                    const params = this.buildTeamFilterParams({
                        startDate: this.startDate,
                        endDate: this.endDate
                    });
                    const reqConfig = controller ? { params, signal: controller.signal } : { params };
                    const [capResp, burnResp] = await Promise.all([
                        this.$http.get("/TeamReport/Capacity", reqConfig),
                        this.$http.get("/TeamReport/BurnDown", reqConfig)
                    ]);
                    this.capacityData = capResp.data;
                    this.burnDownData = burnResp.data;
                } catch (err) {
                    if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
                        return;
                    }
                    if (err.response && err.response.status === 402) {
                        this.openUpgradeModal();
                    } else {
                        console.warn("Failed to load live capacity reports, using fallback mock", err);
                        this.capacityData = this.getMockCapacityData();
                        this.burnDownData = this.getMockBurnDownData();
                    }
                } finally {
                    if (!controller || this.sprintDataAbortController === controller) {
                        this.capacityLoading = false;
                    }
                }
            },
            async loadCommitmentsAsync(controller = null) {
                if (!this.isTeamOrHigher) {
                    this.commitmentsData = this.getMockCommitmentsData();
                    this.commitmentsLoading = false;
                    return;
                }
                this.commitmentsLoading = true;
                try {
                    const reqConfig = {
                        params: this.buildTeamFilterParams({
                            startDate: this.startDate,
                            endDate: this.endDate
                        })
                    };
                    if (controller) reqConfig.signal = controller.signal;
                    const resp = await this.$http.get("/TeamReport/Commitments", reqConfig);
                    this.commitmentsData = resp.data || this.getMockCommitmentsData();
                } catch (err) {
                    if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
                        return;
                    }
                    this.commitmentsData = this.getMockCommitmentsData();
                } finally {
                    if (!controller || this.sprintDataAbortController === controller) {
                        this.commitmentsLoading = false;
                    }
                }
            },
            async loadTeamAttentionAsync(sprintPath, controller = null) {
                if (!this.isTeamOrHigher || !sprintPath) {
                    this.teamAttentionData = null;
                    return;
                }
                this.teamAttentionLoading = true;
                try {
                    const reqConfig = { params: { sprint: sprintPath } };
                    if (controller) reqConfig.signal = controller.signal;
                    const resp = await this.$http.get("/TaskManagement/TeamAttentionSignals", reqConfig);
                    const d = resp.data || {};
                    this.teamAttentionData = {
                        blockedItems: d.blockedItems || [],
                        unestimatedItems: d.unestimatedItems || [],
                        silentBottlenecks: d.silentBottlenecks || [],
                        openWorkByAssignee: d.openWorkByAssignee || []
                    };
                } catch (err) {
                    if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
                        return;
                    }
                    this.teamAttentionData = null;
                } finally {
                    if (!controller || this.sprintDataAbortController === controller) {
                        this.teamAttentionLoading = false;
                    }
                }
            },
            async loadAbsencesAsync(controller = null) {
                if (!this.isTeamOrHigher) {
                    this.absencesData = this.getMockAbsencesData();
                    this.absencesLoading = false;
                    return;
                }
                this.absencesLoading = true;
                try {
                    const reqConfig = {
                        params: this.buildTeamFilterParams({
                            startDate: this.startDate,
                            endDate: this.endDate
                        })
                    };
                    if (controller) reqConfig.signal = controller.signal;
                    const resp = await this.$http.get("/TeamReport/Absences", reqConfig);
                    this.absencesData = resp.data || this.getMockAbsencesData();
                } catch (err) {
                    if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
                        return;
                    }
                    this.absencesData = this.getMockAbsencesData();
                } finally {
                    if (!controller || this.sprintDataAbortController === controller) {
                        this.absencesLoading = false;
                    }
                }
            },

            applyPreset(presetName) {
                this.preset = presetName;
                const now = new Date();
                if (presetName === "thisMonth") {
                    this.startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
                    this.endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
                } else if (presetName === "lastMonth") {
                    this.startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString().slice(0, 10);
                    this.endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)).toISOString().slice(0, 10);
                } else if (presetName === "twoWeeks") {
                    const start = new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000);
                    this.startDate = start.toISOString().slice(0, 10);
                    this.endDate = now.toISOString().slice(0, 10);
                }
                this.loadCapacityReportsAsync();
                this.loadCommitmentsAsync();
                this.loadAbsencesAsync();
            },
            onCustomDateChange() {
                this.preset = "custom";
                this.loadCapacityReportsAsync();
                this.loadCommitmentsAsync();
                this.loadAbsencesAsync();
            },
            onSprintPicked(path, sprint) {
                if (sprint && sprint.from && sprint.to) {
                    this.startDate = new Date(sprint.from).toISOString().slice(0, 10);
                    this.endDate = new Date(sprint.to).toISOString().slice(0, 10);
                    this.preset = "sprint";

                    if (this.sprintDataAbortController) {
                        this.sprintDataAbortController.abort();
                        this.sprintDataAbortController = null;
                    }
                    const controller = new AbortController();
                    this.sprintDataAbortController = controller;

                    this.loadCapacityReportsAsync(controller);
                    this.loadCommitmentsAsync(controller);
                    this.loadAbsencesAsync(controller);
                    this.loadTeamAttentionAsync(path, controller);
                } else if (!sprint) {
                    this.capacityData = null;
                    this.burnDownData = null;
                    this.commitmentsData = null;
                    this.absencesData = null;
                    this.capacityLoading = false;
                    this.commitmentsLoading = false;
                    this.absencesLoading = false;
                }
            },
            getMemberRole(sub) {
                const map = {
                    "Alex Morgan": "Tech Lead & Full-Stack",
                    "Sarah Connor": "Lead Frontend Engineer",
                    "John Reese": "DevOps & Cloud Platform",
                    "Elena Rostova": "QA Automation Lead",
                    "Marcus Vance": "Senior Database Specialist",
                    "Chloe Price": "Junior Frontend Developer"
                };
                return map[sub.fullName] || sub.role || "Software Engineer";
            },
            getInitials(name) {
                if (!name) return "U";
                const parts = name.trim().split(" ");
                if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                return name.substring(0, 2).toUpperCase();
            },
            getCompletionBadgeVariant(pct) {
                if (pct >= 85 && pct <= 115) return "success";
                if (pct < 85) return "warning";
                return "info";
            },
            getCompletionProgressVariant(pct) {
                if (pct >= 85 && pct <= 115) return "success";
                if (pct < 85) return "warning";
                return "primary";
            },
            getStatusBadgeVariant(status) {
                if (status === "OnTrack") return "success";
                if (status === "UnderCapacity") return "warning";
                if (status === "OverCapacity") return "info";
                return "secondary";
            },
            formatStatus(status) {
                if (status === "OnTrack") return "On Track";
                if (status === "UnderCapacity") return "Under Capacity";
                if (status === "OverCapacity") return "Ahead / Over";
                return status;
            },
            formatDateString(dateStr) {
                if (!dateStr) return "";
                const d = new Date(dateStr);
                return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
            },
            getWorkItemTypeIcon(type) {
                const t = (type || "").toLowerCase();
                if (t.includes("story") || t.includes("user story")) return "bi bi-bookmark-star-fill text-primary";
                if (t.includes("bug")) return "bi bi-bug-fill text-danger";
                return "bi bi-check2-circle text-info";
            },
            getStateBadgeVariant(state) {
                const s = (state || "").toLowerCase();
                if (s.includes("done") || s.includes("closed")) return "success";
                if (s.includes("progress")) return "primary";
                if (s.includes("blocked")) return "danger";
                return "secondary";
            },
            getAbsenceBadgeVariant(type) {
                const t = (type || "").toLowerCase();
                if (t.includes("vacation") || t.includes("pto")) return "success";
                if (t.includes("sick")) return "warning";
                if (t.includes("holiday")) return "info";
                if (t.includes("training") || t.includes("conference")) return "primary";
                return "secondary";
            },

            getMockCapacityData() {
                return {
                    totalExpectedHours: 450,
                    totalLoggedHours: 426,
                    totalWorkHours: 342,
                    totalActivityHours: 64,
                    totalAbsenceHours: 20,
                    overallCompletionPercentage: 94.7,
                    totalWorkDays: 10,
                    members: [
                        {
                            accountUid: "11111111-1111-1111-1111-111111111111",
                            fullName: "Alex Morgan",
                            email: "alex.morgan@velocytrack.com",
                            role: "Tech Lead & Full-Stack",
                            picture: "",
                            dailyCapacity: 8,
                            expectedHours: 80,
                            loggedHours: 78.5,
                            workHours: 62.5,
                            activityHours: 14,
                            absenceHours: 0,
                            completionPercentage: 98.1,
                            status: "OnTrack"
                        },
                        {
                            accountUid: "22222222-2222-2222-2222-222222222222",
                            fullName: "Sarah Connor",
                            email: "sarah.connor@velocytrack.com",
                            role: "Lead Frontend Engineer",
                            picture: "",
                            dailyCapacity: 8,
                            expectedHours: 80,
                            loggedHours: 77,
                            workHours: 61,
                            activityHours: 12,
                            absenceHours: 4,
                            completionPercentage: 96.3,
                            status: "OnTrack"
                        },
                        {
                            accountUid: "33333333-3333-3333-3333-333333333333",
                            fullName: "John Reese",
                            email: "john.reese@velocytrack.com",
                            role: "DevOps & Cloud Platform",
                            picture: "",
                            dailyCapacity: 6,
                            expectedHours: 60,
                            loggedHours: 58.5,
                            workHours: 46.5,
                            activityHours: 8,
                            absenceHours: 4,
                            completionPercentage: 97.5,
                            status: "OnTrack"
                        },
                        {
                            accountUid: "44444444-4444-4444-4444-444444444444",
                            fullName: "Elena Rostova",
                            email: "elena.rostova@velocytrack.com",
                            role: "QA Automation Lead",
                            picture: "",
                            dailyCapacity: 8,
                            expectedHours: 80,
                            loggedHours: 64,
                            workHours: 48,
                            activityHours: 8,
                            absenceHours: 8,
                            completionPercentage: 80.0,
                            status: "UnderCapacity"
                        },
                        {
                            accountUid: "55555555-5555-5555-5555-555555555555",
                            fullName: "Marcus Vance",
                            email: "marcus.vance@velocytrack.com",
                            role: "Senior Database Specialist",
                            picture: "",
                            dailyCapacity: 8,
                            expectedHours: 80,
                            loggedHours: 76,
                            workHours: 64,
                            activityHours: 12,
                            absenceHours: 0,
                            completionPercentage: 95.0,
                            status: "OnTrack"
                        },
                        {
                            accountUid: "66666666-6666-6666-6666-666666666666",
                            fullName: "Chloe Price",
                            email: "chloe.price@velocytrack.com",
                            role: "Junior Frontend Developer",
                            picture: "",
                            dailyCapacity: 7,
                            expectedHours: 70,
                            loggedHours: 66,
                            workHours: 56,
                            activityHours: 10,
                            absenceHours: 0,
                            completionPercentage: 94.3,
                            status: "OnTrack"
                        }
                    ]
                };
            },
            getMockBurnDownData() {
                const now = new Date();
                const days = [];
                let expectedCumulative = 0;
                let actualCumulative = 0;

                for (let i = -7; i <= 6; i++) {
                    const d = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
                    const dayOfWeek = d.getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                    if (!isWeekend) {
                        expectedCumulative += 45;
                        if (i <= 0) {
                            actualCumulative += 43.5;
                        }
                    }

                    days.push({
                        date: d.toISOString().slice(0, 10),
                        isWeekend,
                        dailyLoggedHours: (!isWeekend && i <= 0) ? 43.5 : 0,
                        actualCumulativeHours: (i <= 0) ? actualCumulative : 0,
                        expectedCumulativeHours: expectedCumulative
                    });
                }

                return {
                    totalExpectedHours: expectedCumulative,
                    totalLoggedHours: actualCumulative,
                    days
                };
            },
            getMockCommitmentsData() {
                return {
                    totalItems: 7,
                    activeItems: 5,
                    completedItems: 2,
                    totalEstimatedHours: 110,
                    totalCompletedHours: 71.5,
                    totalRemainingHours: 30.5,
                    completionPercentage: 65.0,
                    workloadBalance: [
                        {
                            accountUid: "11111111-1111-1111-1111-111111111111",
                            fullName: "Alex Morgan",
                            email: "alex.morgan@velocytrack.com",
                            role: "Owner",
                            availableCapacityHours: 72,
                            assignedEstimatedHours: 40,
                            assignedRemainingHours: 13.5,
                            completedHours: 26.5,
                            activeDeliverablesCount: 2,
                            loadRatioPercentage: 55.6,
                            loadStatus: "Balanced"
                        },
                        {
                            accountUid: "22222222-2222-2222-2222-222222222222",
                            fullName: "Sarah Connor",
                            email: "sarah.connor@velocytrack.com",
                            role: "Admin",
                            availableCapacityHours: 76,
                            assignedEstimatedHours: 12,
                            assignedRemainingHours: 3,
                            completedHours: 9,
                            activeDeliverablesCount: 1,
                            loadRatioPercentage: 15.8,
                            loadStatus: "HasSpareCapacity"
                        },
                        {
                            accountUid: "44444444-4444-4444-4444-444444444444",
                            fullName: "Elena Rostova",
                            email: "elena.rostova@velocytrack.com",
                            role: "Member",
                            availableCapacityHours: 64,
                            assignedEstimatedHours: 14,
                            assignedRemainingHours: 8,
                            completedHours: 6,
                            activeDeliverablesCount: 1,
                            loadRatioPercentage: 21.9,
                            loadStatus: "Balanced"
                        }
                    ],
                    items: [
                        {
                            workItemId: 1044,
                            title: "Frontend Google Sign-in button & token exchange",
                            workItemType: "Task",
                            state: "In Progress",
                            assignedToName: "Alex Morgan",
                            sprintName: "Sprint 42 - Core Platform",
                            originalEstimateHours: 16,
                            completedHours: 12.5,
                            remainingHours: 3.5,
                            completionPercentage: 78.1
                        },
                        {
                            workItemId: 1053,
                            title: "Overspent capacity progress bar color indicators & warnings",
                            workItemType: "Task",
                            state: "In Progress",
                            assignedToName: "Sarah Connor",
                            sprintName: "Sprint 42 - Core Platform",
                            originalEstimateHours: 12,
                            completedHours: 9,
                            remainingHours: 3,
                            completionPercentage: 75.0
                        },
                        {
                            workItemId: 1060,
                            title: "PostgreSQL DB provider configuration & Docker container service",
                            workItemType: "Task",
                            state: "Done",
                            assignedToName: "John Reese",
                            sprintName: "Sprint 42 - Core Platform",
                            originalEstimateHours: 20,
                            completedHours: 20,
                            remainingHours: 0,
                            completionPercentage: 100.0
                        },
                        {
                            workItemId: 1072,
                            title: "Automated regression test suite for Unified Grid Filter",
                            workItemType: "Task",
                            state: "Active",
                            assignedToName: "Elena Rostova",
                            sprintName: "Sprint 42 - Core Platform",
                            originalEstimateHours: 14,
                            completedHours: 6,
                            remainingHours: 8,
                            completionPercentage: 42.9
                        },
                        {
                            workItemId: 1085,
                            title: "TimeLog and Workspace indexes optimization for heavy aggregations",
                            workItemType: "Task",
                            state: "Active",
                            assignedToName: "Marcus Vance",
                            sprintName: "Sprint 42 - Core Platform",
                            originalEstimateHours: 16,
                            completedHours: 10,
                            remainingHours: 6,
                            completionPercentage: 62.5
                        },
                        {
                            workItemId: 1091,
                            title: "Dark theme contrast gaps in modals and dropdown menus",
                            workItemType: "Bug",
                            state: "Done",
                            assignedToName: "Chloe Price",
                            sprintName: "Sprint 42 - Core Platform",
                            originalEstimateHours: 8,
                            completedHours: 8,
                            remainingHours: 0,
                            completionPercentage: 100.0
                        },
                        {
                            workItemId: 1030,
                            title: "Multi-platform OAuth token refresh background service",
                            workItemType: "User Story",
                            state: "Active",
                            assignedToName: "Alex Morgan",
                            sprintName: "Sprint 42 - Core Platform",
                            originalEstimateHours: 24,
                            completedHours: 14,
                            remainingHours: 10,
                            completionPercentage: 58.3
                        }
                    ]
                };
            },
            getMockAbsencesData() {
                const now = new Date();
                const d1 = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                const d2 = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                const d3 = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                const d4 = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                const d5 = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

                return {
                    totalAbsenceDays: 5,
                    totalAbsenceHours: 38,
                    membersWithAbsenceCount: 4,
                    absences: [
                        {
                            id: "abs-1",
                            accountUid: "44444444-4444-4444-4444-444444444444",
                            memberName: "Elena Rostova",
                            memberEmail: "elena.rostova@velocytrack.com",
                            startDate: d1,
                            endDate: d2,
                            absenceType: "Vacation / PTO",
                            hours: 16,
                            days: 2,
                            status: "Approved",
                            notes: "Annual leave — family vacation"
                        },
                        {
                            id: "abs-2",
                            accountUid: "33333333-3333-3333-3333-333333333333",
                            memberName: "John Reese",
                            memberEmail: "john.reese@velocytrack.com",
                            startDate: d3,
                            endDate: d3,
                            absenceType: "Training / Conference",
                            hours: 6,
                            days: 1,
                            status: "Approved",
                            notes: "Attending KubeCon CloudNative virtual summit"
                        },
                        {
                            id: "abs-3",
                            accountUid: "22222222-2222-2222-2222-222222222222",
                            memberName: "Sarah Connor",
                            memberEmail: "sarah.connor@velocytrack.com",
                            startDate: d4,
                            endDate: d4,
                            absenceType: "Sick Leave",
                            hours: 8,
                            days: 1,
                            status: "Approved",
                            notes: "Medical recovery day"
                        },
                        {
                            id: "abs-4",
                            accountUid: "11111111-1111-1111-1111-111111111111",
                            memberName: "Alex Morgan",
                            memberEmail: "alex.morgan@velocytrack.com",
                            startDate: d5,
                            endDate: d5,
                            absenceType: "Public Holiday",
                            hours: 8,
                            days: 1,
                            status: "Approved",
                            notes: "National Public Holiday"
                        }
                    ]
                };
            }
        }
    };
</script>

<style scoped>
    .config-section-card {
        background-color: var(--vt-card-bg, #ffffff) !important;
        border-color: var(--vt-card-border, #dee2e6) !important;
        border-radius: 12px;
        overflow: hidden;
        transition: box-shadow 0.2s ease;
    }

    .config-section-card:hover {
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05) !important;
    }

    .config-section-card .card-header {
        background-color: var(--vt-card-bg, #ffffff) !important;
        border-color: var(--vt-border, #dee2e6) !important;
    }

    .config-section-card .card-header h5 {
        color: var(--vt-text, #212529) !important;
    }

    .section-accordion-header {
        cursor: pointer;
        user-select: none;
        transition: background-color 0.15s ease-in-out;
    }

    .section-accordion-header:hover {
        background-color: var(--vt-bg, #f8f9fa) !important;
    }

    .section-accordion-header:focus {
        outline: none;
        box-shadow: inset 0 0 0 2px var(--vt-primary, #0284c7);
    }

    .accordion-toggle-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        border-left: 1px solid var(--vt-border, #dee2e6) !important;
        cursor: pointer;
        padding-top: 2px;
        padding-bottom: 2px;
    }

    .accordion-chevron {
        font-size: 1.15rem;
        color: var(--vt-muted, #6c757d);
        transition: transform 0.25s ease-in-out, color 0.15s ease-in-out;
        display: inline-block;
    }

    .accordion-chevron.is-expanded {
        transform: rotate(180deg);
        color: var(--vt-primary, #0284c7);
    }

    .config-section-nav .btn.active {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    }

    .config-section-nav .btn.active .badge-primary {
        background-color: #ffffff !important;
        color: var(--vt-primary, #0284c7) !important;
    }

    .config-section-nav .btn.active .badge-success {
        background-color: #ffffff !important;
        color: #28a745 !important;
    }

    .config-section-nav .btn.active .badge-warning {
        background-color: #ffffff !important;
        color: #856404 !important;
    }

    .team-sprint-scope-card {
        background-color: var(--vt-card-bg, #ffffff) !important;
        border-color: var(--vt-border, #dee2e6) !important;
        border-radius: 12px;
    }

    .section-step-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background-color: rgba(2, 132, 199, 0.12);
        color: var(--vt-primary, #0284c7);
        font-weight: 700;
        font-size: 0.85rem;
    }

    .config-section-nav .btn {
        font-size: 0.82rem;
        transition: all 0.15s ease-in-out;
    }

    .config-section-nav .btn:hover {
        transform: translateY(-1px);
    }

    .team-page-header {
        border-bottom-color: var(--vt-border) !important;
    }

    .team-title {
        color: var(--vt-text);
    }

    .team-control-panel {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border);
    }

    .team-nav-tabs ::v-deep .nav-pills {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border);
        border-radius: 0.75rem;
        padding: 0.35rem;
        gap: 0.35rem;
    }

    .team-nav-tabs ::v-deep .nav-pills .nav-link {
        font-weight: 600;
        font-size: 0.95rem;
        border-radius: 0.5rem;
        color: var(--vt-muted);
        transition: all 0.2s ease;
        padding: 0.65rem 1.25rem;
    }

    .team-nav-tabs ::v-deep .nav-pills .nav-link:hover {
        color: var(--vt-text);
        background: rgba(13, 110, 253, 0.08);
    }

    .team-nav-tabs ::v-deep .nav-pills .nav-link.active {
        background: #0d6efd;
        color: #ffffff !important;
        box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25);
    }

    .team-month-label {
        min-width: 10rem;
        text-align: center;
        color: var(--vt-text);
    }

    .team-member-card {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border) !important;
        border-radius: 0.75rem;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
    }

    .team-member-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
    }

    .team-member-name {
        font-size: 1.15rem;
        color: var(--vt-text);
    }

    .time-progress-card {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border);
        border-radius: 0.75rem;
        padding: 1rem 1.1rem;
        height: 100%;
    }

    .team-progress-card,
    .team-chart-card {
        min-height: 16rem;
    }

    .time-progress-title {
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--vt-muted);
        margin-bottom: 0.85rem;
        text-align: center;
    }

    .team-insight-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.95rem;
    }

    .team-chip {
        display: inline-flex;
        align-items: center;
        border-radius: 999px;
        padding: 0.25rem 0.7rem;
        font-size: 0.78rem;
        font-weight: 600;
    }

    .team-chip-work {
        background: rgba(40, 167, 69, 0.15);
        color: #28a745;
        border: 1px solid rgba(40, 167, 69, 0.25);
    }

    .team-chip-activity {
        background: rgba(13, 110, 253, 0.15);
        color: #0d6efd;
        border: 1px solid rgba(13, 110, 253, 0.25);
    }

    .team-chip-absence {
        background: rgba(111, 66, 193, 0.15);
        color: #6f42c1;
        border: 1px solid rgba(111, 66, 193, 0.25);
    }

    .stat-card {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border) !important;
        border-radius: 12px;
        transition: transform 0.2s ease;
    }

    .stat-card:hover {
        transform: translateY(-2px);
    }

    .team-table-card {
        background: var(--vt-card-bg);
        border: 1px solid var(--vt-border) !important;
        border-radius: 0.75rem;
        overflow: hidden;
    }

    .capacity-sub-tabs ::v-deep .nav-pills .nav-link {
        font-weight: 600;
        border-radius: 8px;
        padding: 0.6rem 1.25rem;
        color: var(--vt-muted);
    }

    .capacity-sub-tabs ::v-deep .nav-pills .nav-link.active {
        background-color: #0d6efd;
        color: #ffffff !important;
    }

    .team-loading {
        min-height: 380px;
        width: 100%;
    }

    .team-coming-soon-card {
        background: var(--vt-card-bg, #ffffff);
        border: 1px solid var(--vt-border, rgba(0, 0, 0, 0.12)) !important;
        border-radius: 16px;
    }

    .coming-soon-icon-badge {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        background: rgba(13, 110, 253, 0.1);
        color: #0d6efd;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2.2rem;
    }

    [data-theme="dark"] .coming-soon-icon-badge {
        background: rgba(13, 110, 253, 0.22);
        color: #60a5fa;
    }

    .coming-soon-pill {
        letter-spacing: 0.5px;
        font-size: 0.72rem;
    }

    .coming-soon-desc {
        font-size: 1rem;
        line-height: 1.6;
    }

    .feature-preview-card {
        background: var(--vt-card-bg, #f8f9fa);
        border: 1px solid var(--vt-border, rgba(0, 0, 0, 0.08));
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .feature-preview-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    }

    [data-theme="dark"] .feature-preview-card {
        background: rgba(255, 255, 255, 0.03);
        border-color: rgba(255, 255, 255, 0.08);
    }

    .feature-icon-box {
        font-size: 1.25rem;
    }

    .mobile-col-caption {
        font-size: 0.65rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--text-muted, #6c757d);
        margin-bottom: 0.2rem;
    }

    @media (max-width: 991.98px) {
        .team-management-page {
            padding-top: 0.75rem !important;
            padding-bottom: 1.5rem !important;
        }
        .team-page-header {
            margin-bottom: 0.85rem !important;
            padding-bottom: 0.65rem !important;
        }
        .team-title {
            font-size: 1.25rem !important;
        }
        .stat-card .display-4 {
            font-size: 1.85rem !important;
        }
        .config-section-card > .card-header {
            padding: 0.65rem 0.85rem !important;
        }
        .config-section-card > .collapse > .card-body,
        .team-commitments-section,
        .team-absences-section,
        .team-capacity-section {
            padding: 0.75rem !important;
        }

        /* Unified Grid Items Card Styling */
        .grid-shell .card {
            border-radius: 0.65rem !important;
            padding: 0.75rem 0.85rem !important;
            background: rgba(30, 41, 59, 0.65) !important;
            border: 1px solid rgba(255, 255, 255, 0.08) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18) !important;
            margin-bottom: 0.65rem !important;
        }

        [data-theme="light"] .grid-shell .card {
            background: #ffffff !important;
            border: 1px solid rgba(0, 0, 0, 0.08) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
        }

        .grid-shell .card > .card-body {
            padding: 0 !important;
        }

        .grid-shell .grid-row {
            display: flex !important;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.45rem 0.6rem;
            padding: 0 !important;
            margin: 0 !important;
        }

        .grid-shell .grid-row > [class*="col"] {
            flex: 0 1 auto !important;
            width: auto !important;
            max-width: 100% !important;
            padding: 0 !important;
        }

        /* Grid 1: Member Workload Breakdown */
        .grid-col-member,
        .grid-col-role {
            order: 1 !important;
        }
        .grid-col-status {
            order: 1 !important;
            margin-left: auto !important;
        }
        .grid-col-dailyCapacity,
        .grid-col-expectedHours,
        .grid-col-loggedHours {
            order: 2 !important;
        }
        .grid-col-progress {
            order: 3 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
        }

        /* Grid 2: Burn-down Days */
        .grid-col-date {
            order: 1 !important;
        }
        .grid-col-dailyLogged {
            order: 1 !important;
            margin-left: auto !important;
        }
        .grid-col-actualCumulative,
        .grid-col-expectedCumulative,
        .grid-col-variance {
            order: 2 !important;
        }

        /* Grid 3: Squad Workload Balance */
        .grid-col-loadStatus {
            order: 1 !important;
            margin-left: auto !important;
        }
        .grid-col-availableCapacityHours,
        .grid-col-assignedEstimatedHours,
        .grid-col-assignedRemainingHours,
        .grid-col-activeDeliverablesCount {
            order: 2 !important;
        }
        .grid-col-allocation {
            order: 3 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
        }

        /* Grid 4: Deliverables */
        .grid-col-workItemId,
        .grid-col-state {
            order: 1 !important;
        }
        .grid-col-assignedTo {
            order: 1 !important;
            margin-left: auto !important;
        }
        .grid-col-item {
            order: 2 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            font-size: 0.95rem;
            font-weight: 600;
            line-height: 1.35;
            margin-top: 0.15rem !important;
            margin-bottom: 0.25rem !important;
        }
        .grid-col-estimate,
        .grid-col-completed,
        .grid-col-remaining {
            order: 3 !important;
        }

        /* Grid 5: Absences */
        .grid-col-type {
            order: 1 !important;
        }
        .grid-col-dates,
        .grid-col-duration {
            order: 2 !important;
        }
        .grid-col-notes,
        .grid-col-description {
            order: 3 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
        }
    }

    .team-dashboard-mobile-noop {
    }
</style>
